import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Gamepad2, Loader2, Map as MapIcon, Pencil, Plus, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
  NumberInput,
  SearchInput,
  Select,
  Switch,
} from "../../../../components/ui";
import { trpc } from "../../../../lib/trpc";

export const Route = createFileRoute("/app/admin/games/")({
  component: GamesListPage,
});

const platformOptions = [
  { value: "", label: "None" },
  { value: "riot", label: "Riot" },
  { value: "steam", label: "Steam" },
  { value: "microsoft", label: "Microsoft" },
  { value: "epic", label: "Epic" },
  { value: "battlenet", label: "Battle.net" },
];

function GamesListPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data: games, isLoading } = trpc.game.adminList.useQuery();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const toggleActive = trpc.game.update.useMutation({
    onSuccess: () => {
      void utils.game.adminList.invalidate();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const filtered = games?.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-wide">Games</h1>
          <p className="text-sm text-surface-400 mt-1">Manage platform games, modes, and maps</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4 mr-2" />
          Create Game
        </Button>
      </div>

      {/* Search */}
      <SearchInput
        placeholder="Search games..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClear={() => setSearch("")}
        className="max-w-sm"
      />

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 rounded-xl bg-surface-800 animate-pulse" />
          ))}
        </div>
      ) : filtered && filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((game) => (
            <button
              key={game.id}
              type="button"
              onClick={() =>
                void navigate({
                  to: "/app/admin/games/$gameId",
                  params: { gameId: game.id },
                })
              }
              className="group relative flex flex-col p-5 rounded-xl bg-surface-800 border border-surface-700/50 hover:border-brand-500/40 transition-all text-left"
            >
              {/* Top row: icon + name */}
              <div className="flex items-center gap-3 mb-3">
                {game.iconUrl ? (
                  <img
                    src={game.iconUrl}
                    alt={game.name}
                    className="size-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="size-10 rounded-lg bg-surface-700 flex items-center justify-center">
                    <Gamepad2 className="size-5 text-surface-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-white truncate">{game.name}</h3>
                  <p className="text-xs text-surface-500 truncate">{game.slug}</p>
                </div>
                <Pencil className="size-4 text-surface-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {game.platform && (
                  <Badge variant="subtle" color="blue" size="sm">
                    {game.platform}
                  </Badge>
                )}
                <Badge variant="subtle" color={game.isActive ? "green" : "red"} size="sm">
                  {game.isActive ? "Active" : "Inactive"}
                </Badge>
                <Badge variant="subtle" color="gray" size="sm">
                  {game.defaultTeamSize}v{game.defaultTeamSize}
                </Badge>
                {game.hasRoles && (
                  <Badge variant="subtle" color="purple" size="sm">
                    Roles
                  </Badge>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 mt-auto pt-3 border-t border-surface-700/30">
                <div className="flex items-center gap-1.5 text-xs text-surface-400">
                  <Gamepad2 className="size-3.5" />
                  <span>{game.modeCount} modes</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-surface-400">
                  <MapIcon className="size-3.5" />
                  <span>{game.mapCount} maps</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-surface-400">
                  <Users className="size-3.5" />
                  <span>{game.clubCount} clubs</span>
                </div>
              </div>

              {/* Active toggle (stop propagation so card click doesn't fire) */}
              <div className="absolute top-4 right-4" onClick={(e) => e.stopPropagation()}>
                <Switch
                  size="sm"
                  checked={game.isActive}
                  onChange={() =>
                    toggleActive.mutate({
                      id: game.id,
                      isActive: !game.isActive,
                    })
                  }
                />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Gamepad2 className="size-10 text-surface-500 mb-3" />
          <h3 className="text-lg font-semibold text-white">No games found</h3>
          <p className="text-sm text-surface-400 mt-1">
            {search ? "Try a different search term" : "Create your first game to get started"}
          </p>
        </div>
      )}

      {/* Create Game Dialog */}
      <CreateGameDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function CreateGameDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [platform, setPlatform] = useState("");
  const [teamSize, setTeamSize] = useState(5);
  const [hasRoles, setHasRoles] = useState(false);

  const createGame = trpc.game.create.useMutation({
    onSuccess: () => {
      toast.success("Game created");
      void utils.game.adminList.invalidate();
      onOpenChange(false);
      resetForm();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const resetForm = () => {
    setName("");
    setSlug("");
    setPlatform("");
    setTeamSize(5);
    setHasRoles(false);
  };

  const handleNameChange = (val: string) => {
    setName(val);
    if (!slug || slug === slugify(name)) {
      setSlug(slugify(val));
    }
  };

  const handleSubmit = () => {
    createGame.mutate({
      name,
      slug: slug || undefined,
      platform: (platform || null) as "battlenet" | "epic" | "microsoft" | "riot" | "steam" | null,
      defaultTeamSize: teamSize,
      hasRoles,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader subtitle="Add a new game to the platform">Create Game</DialogHeader>
        <DialogBody className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1.5">Name</label>
            <Input
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Valorant"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1.5">Slug</label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="auto-generated"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1.5">Platform</label>
            <Select
              options={platformOptions}
              value={platform}
              onChange={setPlatform}
              placeholder="None"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1.5">
              Default Team Size
            </label>
            <NumberInput
              value={teamSize}
              min={1}
              max={100}
              onChange={(e) => setTeamSize(Number(e.target.value) || 5)}
            />
          </div>
          <Switch
            label="Has Roles"
            description="Players select roles (e.g. LoL positions)"
            checked={hasRoles}
            onChange={(e) => setHasRoles(e.target.checked)}
          />
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={!name.trim() || createGame.isPending}>
            {createGame.isPending ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
